/**
 * Batch Status Badge Component
 * Displays production batch status with color-coded visual indicators
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 *
 * @description
 * Visual badge component for displaying production batch statuses with:
 * - Color-coded statuses (Gray/Blue/Green/Red)
 * - Multiple size variants (sm/md/lg)
 * - Animated processing state with pulse effect
 * - Cancelled state with strikethrough
 * - Full accessibility support (ARIA, dark mode)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BatchStatusBadge status="MANUFACTURED" />
 *
 * // Large size
 * <BatchStatusBadge status="PROCESSING" size="lg" />
 *
 * // Custom styling
 * <BatchStatusBadge status="CREATED" className="ml-2" />
 * ```
 */

import { cn } from '@/lib/utils';

export type BatchStatus = 'CREATED' | 'PROCESSING' | 'MANUFACTURED' | 'READY_FOR_SALE' | 'CANCELLED' | 'ALLOCATED' | 'SOLD' | 'CONSUMED';
type Size = 'sm' | 'md' | 'lg';

export interface BatchStatusBadgeProps {
    /**
     * Production batch status to display
     */
    status: BatchStatus;

    /**
     * Visual size of the badge
     * @default 'md'
     */
    size?: Size;

    /**
     * Additional CSS classes
     */
    className?: string;
}

/**
 * Status configuration with colors and labels
 */
const statusConfig: Record<BatchStatus, {
    label: string;
    bgColor: string;
    textColor: string;
    darkBgColor: string;
    darkTextColor: string;
    animate?: boolean;
    strikethrough?: boolean;
}> = {
    CREATED: {
        label: 'Created',
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-800',
        darkBgColor: 'dark:bg-slate-800',
        darkTextColor: 'dark:text-slate-200'
    },
    PROCESSING: {
        label: 'Processing',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        darkBgColor: 'dark:bg-blue-900',
        darkTextColor: 'dark:text-blue-200',
        animate: true
    },
    MANUFACTURED: {
        label: 'Manufactured',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        darkBgColor: 'dark:bg-green-900',
        darkTextColor: 'dark:text-green-200'
    },
    READY_FOR_SALE: {
        label: 'Ready for Sale',
        bgColor: 'bg-emerald-100',
        textColor: 'text-emerald-800',
        darkBgColor: 'dark:bg-emerald-900',
        darkTextColor: 'dark:text-emerald-200'
    },
    CANCELLED: {
        label: 'Cancelled',
        bgColor: 'bg-red-100',
        textColor: 'text-red-600',
        darkBgColor: 'dark:bg-red-900',
        darkTextColor: 'dark:text-red-400',
        strikethrough: true
    },
    ALLOCATED: {
        label: 'Allocated',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        darkBgColor: 'dark:bg-purple-900',
        darkTextColor: 'dark:text-purple-200'
    },
    SOLD: {
        label: 'Sold',
        bgColor: 'bg-indigo-100',
        textColor: 'text-indigo-800',
        darkBgColor: 'dark:bg-indigo-900',
        darkTextColor: 'dark:text-indigo-200'
    },
    CONSUMED: {
        label: 'Consumed',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-800',
        darkBgColor: 'dark:bg-amber-900',
        darkTextColor: 'dark:text-amber-200'
    }
};

/**
 * Size configuration for badge dimensions
 */
const sizeConfig: Record<Size, {
    height: string;
    padding: string;
    fontSize: string;
}> = {
    sm: {
        height: 'h-5',
        padding: 'px-2',
        fontSize: 'text-xs'
    },
    md: {
        height: 'h-6',
        padding: 'px-2.5',
        fontSize: 'text-sm'
    },
    lg: {
        height: 'h-8',
        padding: 'px-3',
        fontSize: 'text-base'
    }
};

/**
 * Batch Status Badge Component
 *
 * @implements Status Colors (Gray/Blue/Green/Red with dark mode)
 * @implements Size Variants (sm/md/lg)
 * @implements Processing Animation (pulse effect)
 * @implements Cancelled Styling (strikethrough)
 * @implements Accessibility (role, aria-label, dark mode)
 */
export function BatchStatusBadge({
    status,
    size = 'md',
    className
}: BatchStatusBadgeProps) {
    // Fallback for unknown statuses
    const defaultConfig = {
        label: status || 'Unknown',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        darkBgColor: 'dark:bg-gray-800',
        darkTextColor: 'dark:text-gray-200'
    };

    const statusInfo = statusConfig[status] || defaultConfig;
    const sizeInfo = sizeConfig[size];

    const ariaLabel = `Production batch status: ${statusInfo.label}`;

    return (
        <span
            role="status"
            aria-label={ariaLabel}
            className={cn(
                // Base styles
                'inline-flex items-center justify-center rounded-full font-medium',

                // Size variant styles
                sizeInfo.height,
                sizeInfo.padding,
                sizeInfo.fontSize,

                // Status colors (light mode)
                statusInfo.bgColor,
                statusInfo.textColor,

                // Dark mode support
                statusInfo.darkBgColor,
                statusInfo.darkTextColor,

                // Processing animation
                statusInfo.animate && 'animate-pulse',

                // Custom classes
                className
            )}
        >
            <span className={cn(statusInfo.strikethrough && 'line-through')}>
                {statusInfo.label}
            </span>
        </span>
    );
}

export default BatchStatusBadge;
